import { useEffect, useId, useRef, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { useJournals, useOverview, usePostBooks, type Pending } from "@/api/books";
import { BooksGuide } from "@/components/books/BooksGuide";
import { RecordLink } from "@/components/books/BooksLink";
import { groupPending, pendingWords, rateHref } from "@/components/books/links";
import { RequestError } from "@/components/RequestError";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button, buttonClass } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { formatDate, formatInr } from "@/lib/format";

const RECENT = 6;

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 rounded-lg border bg-card p-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="break-words text-lg font-semibold text-primary">{value}</p>
    </div>
  );
}

/** A titled list in a card; its heading names it for screen readers. */
function Section({ title, count, children }: { title: string; count?: number; children: ReactNode }) {
  const id = useId();
  return (
    <section aria-labelledby={id} className="rounded-lg border bg-card">
      <h3 id={id} className="flex items-center gap-2 border-b px-3 py-2 text-sm font-semibold">
        {title}
        {count !== undefined && <Badge variant="warning">{count}</Badge>}
      </h3>
      {children}
    </section>
  );
}

function PendingRow({ row }: { row: Pending }) {
  return (
    <li className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <RecordLink source={row} label={row.label} className="font-medium" />
        <p className="text-sm text-muted-foreground">{pendingWords(row)}</p>
      </div>
      {row.reason === "rate_missing" && row.currency && row.on && (
        <Link to={rateHref(row.currency, row.on)} className={buttonClass("outline", "sm")}>
          Add the {row.currency} rate
        </Link>
      )}
    </li>
  );
}

/** What the books could not take yet, grouped by kind of record, each with its fix. */
function NotYetBooked({ pending }: { pending: Pending[] }) {
  if (pending.length === 0) {
    return (
      <EmptyState
        title="Everything is in the books"
        description="Every invoice, payment, bill and transfer that can be valued has its entry."
      />
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <h2 className="text-base font-semibold">Not in the books yet</h2>
      {groupPending(pending).map((group) => (
        <Section key={group.title} title={group.title} count={group.rows.length}>
          <ul className="divide-y">
            {group.rows.map((row) => (
              <PendingRow key={`${row.source_kind}:${row.source_id}`} row={row} />
            ))}
          </ul>
        </Section>
      ))}
    </div>
  );
}

/** The latest entries the app derived by itself: the records flowing in. */
function RecentlyBooked() {
  const recent = useJournals({ kind: "auto", limit: RECENT });
  if (!recent.data || recent.data.length === 0) return <RequestError error={recent.error} />;
  return (
    <Section title="Latest entries from your records">
      <ul className="divide-y">
        {recent.data.map((row) => (
          <li key={row.id} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 p-3 text-sm">
            <span className="min-w-0">
              <RecordLink source={row} label={row.source_label ?? row.narration} className="font-medium" />
              <span className="text-muted-foreground"> · {formatDate(row.entry_date)}</span>
            </span>
            <span className="whitespace-nowrap text-muted-foreground tabular-nums">
              {row.label} · {formatInr(row.total_inr)}
            </span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

export function OverviewPanel() {
  const overview = useOverview();
  const post = usePostBooks();
  const postedOnOpen = useRef(false);

  // The page opens current: whatever can be valued is posted once on arrival.
  useEffect(() => {
    if (postedOnOpen.current) return;
    postedOnOpen.current = true;
    post.mutate(undefined);
  }, [post]);

  const pending = overview.data?.pending ?? [];
  const needsRate = pending.some((row) => row.reason === "rate_missing");

  return (
    <div className="flex flex-col gap-4">
      <RequestError error={overview.error ?? post.error} />
      {overview.data && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Figure label="Financial year" value={`FY ${overview.data.fy_code}`} />
          <Figure label="Entries posted" value={String(overview.data.entries)} />
          <Figure
            label="Books locked through"
            value={overview.data.locked_through ? formatDate(overview.data.locked_through) : "Not locked"}
          />
          <Figure label="Last posted" value={formatDate(overview.data.last_posted_at)} />
        </div>
      )}
      <BooksGuide />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Entries are derived from invoices, payments, bills, transfers and opening balances. A
          record that changed is reversed and posted again; nothing is edited in place.
        </p>
        <Button size="sm" onClick={() => post.mutate(undefined)} disabled={post.isPending}>
          <RefreshCw className="h-4 w-4" aria-hidden />
          {post.isPending ? "Posting…" : "Post now"}
        </Button>
      </div>
      {post.data && (post.data.posted > 0 || post.data.reversed > 0) && (
        <Alert variant="success" title={`${post.data.posted} posted, ${post.data.reversed} reversed`} />
      )}
      {needsRate && (
        <Alert variant="warning" title="Some records wait for an exchange rate">
          The books value every foreign amount at a rate a director typed for that day or an
          earlier one. Nothing is guessed: add the rate and the record goes in by itself.
        </Alert>
      )}
      {overview.data && <NotYetBooked pending={pending} />}
      <RecentlyBooked />
    </div>
  );
}
